/**
 * BaselinePanel — 7-day anomaly baseline + suggested thresholds (suggest→approve).
 *
 * Shows, per baselined metric (elastic `count` / simple `latency_ms`):
 *   - baseline mean ± stddev, p95, sample count, last-computed time
 *   - a history chart of the metric vs its baseline mean band (reuses @ui Chart)
 *   - a status donut (elastic only) from the latest cycle
 *   - "Apply as threshold" buttons that seed a fixed-threshold ($gte) alarm at
 *     mean+2σ (warning) / mean+3σ (critical) — the human clicks to approve.
 */
import { TrendingUp } from 'lucide-react';
import { Button, Card, CardBody } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Chart, PieChart } from '@vnetwork/ui/charts';
import type { BaselineMetricCoverage, BaselineResponse, ProbeResult, WatchpointType } from '@/api/healthcheck';
import type { ConditionRow } from '../alarms/condition-serializer';

interface Props {
  watchpointType: WatchpointType;
  baseline: BaselineResponse | undefined;
  metrics: ProbeResult[];
  /** The elastic monitor's group-by dimension (e.g. "status") — targets per-bucket alarms. */
  dimension?: string;
  /** Seed a $gte threshold alarm from a suggested value (opens the alarm form). */
  onApplyThreshold: (rows: ConditionRow[]) => void;
}

// Metric keys that are the monitor total (vs a per-dimension bucket value).
const PRIMARY_KEYS = new Set(['count', 'latency_ms']);
// Cap the per-bucket table so a high-cardinality dimension can't flood the panel.
const MAX_BUCKET_ROWS = 12;

const fmt = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));
const clock = (iso: string) => {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
};

/** Per-cycle metric value in baseline units: elastic → Σ data_items.count, else latency. */
function metricValue(r: ProbeResult, metricKey: string): number | null {
  if (metricKey === 'latency_ms') return typeof r.latency_ms === 'number' ? r.latency_ms : null;
  const m = (r.metadata && typeof r.metadata === 'object' ? r.metadata : {}) as Record<string, unknown>;
  const items = Array.isArray(m.data_items) ? (m.data_items as Record<string, unknown>[]) : null;
  if (!items) return null;
  return items.reduce((s, it) => s + (Number(it.count) || 0), 0);
}

/** Status-class donut slices from the latest elastic cycle grouped by `status`. */
function statusDonut(metrics: ProbeResult[]): { name: string; value: number; color: string }[] {
  const latest = metrics[0];
  const m = (latest?.metadata && typeof latest.metadata === 'object' ? latest.metadata : {}) as Record<string, unknown>;
  const items = Array.isArray(m.data_items) ? (m.data_items as Record<string, unknown>[]) : [];
  const palette: Record<string, string> = {
    '2xx': 'var(--vn-color-green-400)', '3xx': 'var(--vn-color-blue-500)',
    '4xx': 'var(--vn-color-amber-400)', '5xx': 'var(--vn-color-red-400)',
  };
  const buckets: Record<string, number> = {};
  for (const it of items) {
    const s = String(it.status ?? '');
    if (!/^[1-5]/.test(s)) continue;
    const cls = `${s[0]}xx`;
    buckets[cls] = (buckets[cls] ?? 0) + (Number(it.count) || 0);
  }
  return Object.entries(buckets).map(([name, value]) => ({ name, value, color: palette[name] }));
}

export function BaselinePanel({ watchpointType, baseline, metrics, dimension, onApplyThreshold }: Props) {
  if (!baseline || baseline.status === 'insufficient_data' || baseline.metrics.length === 0) {
    return (
      <Card flat>
        <CardBody>
          <EmptyState
            icon={<TrendingUp size={20} />}
            title="Baseline accruing"
            description="Not enough samples yet. The 7-day baseline + suggested thresholds appear once the monitor has collected enough checks."
            size="sm"
          />
        </CardBody>
      </Card>
    );
  }

  const isElastic = watchpointType === 'elastic';
  const primary = baseline.metrics.find((m) => PRIMARY_KEYS.has(m.metric_key));
  const primaryCoverage = primary
    ? baseline.coverage?.metrics.find((c) => c.metric_key === primary.metric_key)
    : undefined;
  const buckets = baseline.metrics
    // Exclude primary totals and the derived concentration metrics
    // (derived:top1_count / derived:distinct_count) — those are anomaly-only
    // ($sigma) signals, not per-dimension buckets with a fixed-threshold Apply.
    .filter((m) => !PRIMARY_KEYS.has(m.metric_key) && !m.metric_key.startsWith('derived:'))
    .sort((a, b) => b.mean - a.mean)
    .slice(0, MAX_BUCKET_ROWS);
  const donut = isElastic ? statusDonut(metrics) : [];

  // Total-traffic threshold vs per-bucket ("<dim> = <value> AND count ≥ X").
  const applyTotal = (value: number): ConditionRow[] => [
    { field: primary?.metric_key ?? 'count', operator: '$gte', value },
  ];
  const applyBucket = (bucketVal: string, value: number): ConditionRow[] =>
    dimension
      ? [{ field: dimension, operator: '$eq', value: bucketVal }, { field: 'count', operator: '$gte', value }]
      : [{ field: 'count', operator: '$gte', value }];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {primary && (
        <Card flat>
          <CardBody>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
              <span style={{ fontWeight: 600 }}>
                {primary.metric_key} baseline{' '}
                <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400, fontSize: 'var(--vn-font-size-sm)' }}>
                  μ {fmt(primary.mean)} ± {fmt(primary.stddev)}
                  {primary.p95 != null ? ` · p95 ${fmt(primary.p95)}` : ''} · {primary.sample_count} samples · {primary.window_days}d
                </span>
              </span>
              <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                <Button size="sm" variant="secondary" onClick={() => onApplyThreshold(applyTotal(primary.suggested.warning))}>
                  Apply warning ≥ {fmt(primary.suggested.warning)}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => onApplyThreshold(applyTotal(primary.suggested.critical))}>
                  Apply critical ≥ {fmt(primary.suggested.critical)}
                </Button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: donut.length ? 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))' : '1fr', gap: 'var(--vn-space-4)', marginTop: 'var(--vn-space-3)' }}>
              <PrimaryChart metrics={metrics} metricKey={primary.metric_key} mean={primary.mean} />
              {donut.length > 0 && <PieChart data={donut} donut showLegend height={160} />}
            </div>

            {primaryCoverage && <CoverageStrip cov={primaryCoverage} />}
          </CardBody>
        </Card>
      )}

      {buckets.length > 0 && (
        <Card flat>
          <CardBody>
            <div style={{ fontWeight: 600, marginBottom: 'var(--vn-space-2)' }}>
              Per-bucket baselines{' '}
              <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400, fontSize: 'var(--vn-font-size-sm)' }}>
                anomaly detection per {dimension ?? 'bucket'}
              </span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--vn-font-size-sm)' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--vn-fg-muted)' }}>
                  <th style={{ padding: '4px 8px' }}>{dimension ?? 'Bucket'}</th>
                  <th style={{ padding: '4px 8px' }}>μ ± σ</th>
                  <th style={{ padding: '4px 8px' }}>p95</th>
                  <th style={{ padding: '4px 8px' }}>Samples</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Suggested threshold</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((m) => (
                  <tr key={m.metric_key} style={{ borderTop: '1px solid var(--vn-border-subtle)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 500 }}>{m.metric_key}</td>
                    <td style={{ padding: '6px 8px' }}>{fmt(m.mean)} ± {fmt(m.stddev)}</td>
                    <td style={{ padding: '6px 8px' }}>{m.p95 != null ? fmt(m.p95) : '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{m.sample_count}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button size="sm" variant="ghost" onClick={() => onApplyThreshold(applyBucket(m.metric_key, m.suggested.warning))}>
                        warn ≥ {fmt(m.suggested.warning)}
                      </Button>{' '}
                      <Button size="sm" variant="ghost" onClick={() => onApplyThreshold(applyBucket(m.metric_key, m.suggested.critical))}>
                        crit ≥ {fmt(m.suggested.critical)}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

/**
 * 24-cell hour strip showing which hours have a seasonal (per-hour) baseline.
 * Cells are laid out in the viewer's LOCAL hour order; the API's hours are UTC
 * time_buckets, converted per cell. Uncovered hours fall back to the flat
 * baseline at eval time, where drop alerts are suppressed — that consequence is
 * what the strip makes visible.
 */
function CoverageStrip({ cov }: { cov: BaselineMetricCoverage }) {
  const offset = Math.round(-new Date().getTimezoneOffset() / 60);
  const pooled = new Set(cov.hours_pooled);
  const weekday = new Set(cov.hours_weekday);
  const weekend = new Set(cov.hours_weekend);
  const cells = Array.from({ length: 24 }, (_, local) => {
    const utc = (((local - offset) % 24) + 24) % 24;
    const kinds = [pooled.has(utc) && 'pooled', weekday.has(utc) && 'weekday', weekend.has(utc) && 'weekend']
      .filter(Boolean)
      .join(' · ');
    return { local, utc, covered: kinds.length > 0, kinds };
  });
  const nCovered = cells.filter((c) => c.covered).length;
  const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;

  return (
    <div style={{ marginTop: 'var(--vn-space-3)' }}>
      <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', marginBottom: 4 }}>
        Seasonal coverage {nCovered}/24h ({cov.status})
        {nCovered < 24 && ' — uncovered hours use the flat baseline; drop alerts are suppressed there'}
      </div>
      <div style={{ display: 'flex', gap: 2 }}>
        {cells.map((c) => (
          <div
            key={c.local}
            title={
              c.covered
                ? `${hh(c.local)} local (UTC ${c.utc}) — covered: ${c.kinds}`
                : `${hh(c.local)} local (UTC ${c.utc}) — no seasonal baseline yet (flat fallback, drops suppressed)`
            }
            style={{
              flex: 1,
              height: 10,
              borderRadius: 2,
              background: c.covered ? 'var(--vn-color-green-400)' : 'var(--vn-border-subtle)',
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--vn-fg-muted)', marginTop: 2 }}>
        <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
      </div>
    </div>
  );
}

/** History-vs-baseline-μ area chart for the primary (total) metric. */
function PrimaryChart({ metrics, metricKey, mean }: { metrics: ProbeResult[]; metricKey: string; mean: number }) {
  const data = [...metrics]
    .reverse()
    .map((r) => ({ t: clock(r.timestamp), y: metricValue(r, metricKey), mean }))
    .filter((d) => d.y != null);
  if (data.length === 0) {
    return <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>No recent history.</span>;
  }
  return (
    <Chart
      type="area"
      data={data}
      xKey="t"
      series={[{ key: 'y', label: metricKey }, { key: 'mean', label: 'baseline μ' }]}
      height={200}
      valueFormatter={(v) => (v == null ? '' : fmt(Number(v)))}
    />
  );
}
